import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { api } from '../api';
import RecoveryCodesModal from '../components/RecoveryCodesModal';
import Logo from '../components/Logo';

export default function Login({ onLogin }) {
  const [user, setUser]           = useState('');
  const [pass, setPass]           = useState('');
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [step, setStep]           = useState('credentials'); // 'credentials' | 'mfa' | 'mfa_setup' | 'terms'
  const [mfaToken, setMfaToken]   = useState('');
  const [mfaCode, setMfaCode]     = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [setupToken, setSetupToken]   = useState('');
  const [setupSecret, setSetupSecret] = useState('');
  const [setupUri, setSetupUri]       = useState('');
  const [setupCode, setSetupCode]     = useState('');
  const [showForgot, setShowForgot]   = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent]   = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [recoveryCodesModal, setRecoveryCodesModal] = useState(null);
  const [pendingLoginResult, setPendingLoginResult] = useState(null);
  const [acceptanceToken, setAcceptanceToken] = useState('');
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [checkedDocs, setCheckedDocs] = useState({});

  useEffect(() => {
    const reason = sessionStorage.getItem('logout_reason');
    if (reason === 'inactivity') {
      setError('Tu sesión expiró por inactividad. Ingresá nuevamente.');
      sessionStorage.removeItem('logout_reason');
    }
  }, []);

  const handleForgot = async () => {
    if (!forgotEmail) return;
    setForgotLoading(true);
    try {
      await api.forgotPassword(forgotEmail);
      setForgotSent(true);
    } catch (e) {
      setForgotSent(true); // Always show success to avoid enumeration
    } finally {
      setForgotLoading(false);
    }
  };

  const handleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const result = await api.login(user, pass);
      if (result.mfa_setup_required) {
        setSetupToken(result.setup_token);
        const setup = await api.mfaSetupForced(result.setup_token);
        setSetupSecret(setup.secret);
        setSetupUri(setup.uri);
        setStep('mfa_setup');
        return;
      }
      if (result.mfa_required) {
        setMfaToken(result.mfa_token);
        setStep('mfa');
        return;
      }
      if (result.terms_acceptance_required) {
        setAcceptanceToken(result.acceptance_token);
        setPendingDocuments(result.pending_documents);
        setStep('terms');
        return;
      }
      localStorage.setItem('token', result.access_token);
      onLogin(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMfa = async () => {
    setError('');
    setLoading(true);
    try {
      const result = await api.loginMfa(mfaToken, mfaCode);
      if (result.terms_acceptance_required) {
        setAcceptanceToken(result.acceptance_token);
        setPendingDocuments(result.pending_documents);
        setStep('terms');
        return;
      }
      localStorage.setItem('token', result.access_token);
      onLogin(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupConfirm = async () => {
    setError('');
    setLoading(true);
    try {
      const result = await api.mfaSetupForcedConfirm(setupToken, setupSecret, setupCode);
      if (result.recovery_codes) {
        // Retener el login hasta que confirme haber guardado los códigos —
        // onLogin puede desmontar esta pantalla.
        setPendingLoginResult(result);
        setRecoveryCodesModal(result.recovery_codes);
      } else {
        localStorage.setItem('token', result.access_token);
        onLogin(result);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const closeRecoveryCodesModal = () => {
    setRecoveryCodesModal(null);
    if (pendingLoginResult) {
      if (pendingLoginResult.terms_acceptance_required) {
        setAcceptanceToken(pendingLoginResult.acceptance_token);
        setPendingDocuments(pendingLoginResult.pending_documents);
        setStep('terms');
      } else {
        localStorage.setItem('token', pendingLoginResult.access_token);
        onLogin(pendingLoginResult);
      }
      setPendingLoginResult(null);
    }
  };

  const handleAcceptTerms = async () => {
    setError('');
    setLoading(true);
    try {
      const acceptances = pendingDocuments.map(d => ({ document: d.document, version: d.version }));
      const result = await api.acceptTerms(acceptanceToken, acceptances);
      localStorage.setItem('token', result.access_token);
      onLogin(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExitTerms = () => {
    setAcceptanceToken('');
    setPendingDocuments([]);
    setCheckedDocs({});
    setStep('credentials');
    setError('');
  };

  const inputStyle = { width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '12px 16px', fontFamily: 'var(--mono)', fontSize: 14, outline: 'none' };
  const btnStyle = {
    width: '100%', background: loading ? 'var(--accent2)' : 'var(--accent)',
    color: 'var(--bg)', border: 'none', padding: 14, fontFamily: 'var(--mono)',
    fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
    cursor: loading ? 'wait' : 'pointer', marginTop: 8,
    clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))',
  };
  const lblStyle = { display: 'block', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8, fontFamily: 'var(--mono)' };

  // The card width expands for the setup step to fit the QR code
  const cardWidth = step === 'mfa_setup' ? 460 : 400;

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'var(--bg)', position: 'relative', overflow: 'hidden'
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'linear-gradient(var(--login-grid) 1px, transparent 1px), linear-gradient(90deg, var(--login-grid) 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }} />
      <div style={{
        position: 'absolute', width: 600, height: 600,
        background: 'radial-gradient(circle, var(--login-glow) 0%, transparent 70%)',
        top: -100, left: -100, pointerEvents: 'none'
      }} />

      <div style={{
        position: 'relative', zIndex: 1, width: cardWidth,
        background: 'var(--surface)', border: '1px solid var(--border)', padding: 48,
        clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))'
      }}>
        <Logo size={26} />
        <div className="ob-tagline" style={{ marginTop: 14, marginBottom: 36 }}>
          CLOUD BACKUP AS A SERVICE
        </div>

        {error && (
          <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 20 }}>
            {error}
          </div>
        )}

        {step === 'credentials' && (
          <>
            <div style={{ marginBottom: 20 }}>
              <label style={lblStyle}>Usuario</label>
              <input type="text" value={user} autoComplete="off" onChange={e => setUser(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={lblStyle}>Contraseña</label>
              <input type="password" value={pass} autoComplete="new-password" onChange={e => setPass(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLogin()} style={inputStyle} />
            </div>
            <button onClick={handleLogin} disabled={loading} style={btnStyle}>
              {loading ? 'INGRESANDO...' : 'INGRESAR →'}
            </button>

            {!showForgot ? (
              <button
                onClick={() => setShowForgot(true)}
                style={{
                  background: 'none', border: 'none', color: 'var(--muted)',
                  fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer',
                  marginTop: 12, textDecoration: 'underline', width: '100%'
                }}
              >
                ¿Olvidaste tu contraseña?
              </button>
            ) : forgotSent ? (
              <div style={{ marginTop: 16, fontSize: 12, color: 'var(--green)',
                            fontFamily: 'var(--mono)', textAlign: 'center' }}>
                Si el email existe, recibirás un enlace en los próximos minutos.
              </div>
            ) : (
              <div style={{ marginTop: 16 }}>
                <input
                  type="email"
                  placeholder="Tu email"
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  autoComplete="off"
                  style={{ width: '100%', padding: '10px 12px', background: 'var(--bg)',
                           border: '1px solid var(--border)', color: 'var(--text)',
                           fontFamily: 'var(--mono)', fontSize: 13, outline: 'none',
                           boxSizing: 'border-box', marginBottom: 8 }}
                />
                <button
                  onClick={handleForgot}
                  disabled={forgotLoading || !forgotEmail}
                  style={{ width: '100%', background: 'transparent',
                           border: '1px solid var(--border)', color: 'var(--muted)',
                           padding: '10px 0', fontFamily: 'var(--mono)', fontSize: 12,
                           cursor: 'pointer', letterSpacing: 1 }}
                >
                  {forgotLoading ? 'ENVIANDO...' : 'ENVIAR ENLACE →'}
                </button>
                <button
                  onClick={() => setShowForgot(false)}
                  style={{ background: 'none', border: 'none', color: 'var(--muted)',
                           fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer',
                           marginTop: 8, width: '100%' }}
                >
                  cancelar
                </button>
              </div>
            )}
          </>
        )}

        {step === 'mfa' && (
          <>
            <div style={{ marginBottom: 8, fontSize: 13, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
              Verificación en dos pasos
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={lblStyle}>{useRecoveryCode ? 'Código de recuperación' : 'Código de autenticación'}</label>
              <input
                type="text"
                value={mfaCode}
                onChange={e => setMfaCode(
                  useRecoveryCode
                    ? e.target.value.toUpperCase().slice(0, 9)
                    : e.target.value.replace(/\D/g, '').slice(0, 6)
                )}
                onKeyDown={e => e.key === 'Enter' && handleMfa()}
                placeholder={useRecoveryCode ? 'XXXX-XXXX' : '000000'}
                maxLength={useRecoveryCode ? 9 : 6}
                style={{ ...inputStyle, letterSpacing: useRecoveryCode ? 4 : 8, fontSize: useRecoveryCode ? 16 : 20, textAlign: 'center' }}
                autoFocus
              />
              <div style={{ marginTop: 8, fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
                {useRecoveryCode ? 'Ingresá uno de tus códigos de recuperación de un solo uso' : 'Ingresá el código de tu app autenticadora'}
              </div>
            </div>
            <button onClick={handleMfa} disabled={loading || mfaCode.replace(/-/g, '').length < (useRecoveryCode ? 8 : 6)} style={btnStyle}>
              {loading ? 'VERIFICANDO...' : 'VERIFICAR →'}
            </button>
            <button
              onClick={() => { setUseRecoveryCode(v => !v); setMfaCode(''); setError(''); }}
              style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--muted)',
                       fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer',
                       marginTop: 12, textDecoration: 'underline' }}
            >
              {useRecoveryCode ? '← Usar código de la app autenticadora' : '¿No tenés acceso a tu app de autenticación?'}
            </button>
            <button onClick={() => { setStep('credentials'); setError(''); setMfaCode(''); setUseRecoveryCode(false); }}
              style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--muted)', padding: '10px 0 0', fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer', letterSpacing: 1 }}>
              ← Volver
            </button>
          </>
        )}

        {step === 'mfa_setup' && (
          <>
            <div style={{ marginBottom: 4, fontSize: 14, fontFamily: 'var(--mono)', color: 'var(--text)' }}>
              Configuración de autenticación requerida
            </div>
            <div style={{ marginBottom: 20, fontSize: 12, color: 'var(--muted)' }}>
              Tu organización requiere MFA. Escaneá el QR con Google Authenticator o Microsoft Authenticator.
            </div>
            {setupUri && (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                <div style={{ padding: 12, background: '#FFFFFF' }}>
                  <QRCodeSVG
                    value={setupUri}
                    size={200}
                    bgColor="#FFFFFF"
                    fgColor="#000000"
                    level="M"
                    marginSize={2}
                    role="img"
                    aria-label="Código QR para configurar MFA"
                  />
                </div>
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <label style={lblStyle}>Clave manual</label>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)', padding: '8px 12px', background: 'var(--bg)', border: '1px solid var(--border)', letterSpacing: 2, wordBreak: 'break-all' }}>
                {setupSecret}
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={lblStyle}>Confirmar con código</label>
              <input
                type="text"
                value={setupCode}
                onChange={e => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={e => e.key === 'Enter' && setupCode.length === 6 && handleSetupConfirm()}
                placeholder="000000"
                maxLength={6}
                style={{ ...inputStyle, letterSpacing: 8, fontSize: 20, textAlign: 'center' }}
                autoFocus
              />
            </div>
            <button onClick={handleSetupConfirm} disabled={loading || setupCode.length < 6} style={{
              ...btnStyle,
              background: loading || setupCode.length < 6 ? 'var(--accent2)' : 'var(--accent)',
              cursor: setupCode.length < 6 ? 'not-allowed' : 'pointer',
              opacity: setupCode.length < 6 ? 0.6 : 1,
            }}>
              {loading ? 'ACTIVANDO...' : 'ACTIVAR Y CONTINUAR →'}
            </button>
          </>
        )}

        {step === 'terms' && (
          <>
            <div style={{ marginBottom: 4, fontSize: 14, fontFamily: 'var(--mono)', color: 'var(--text)' }}>
              Aceptación de términos requerida
            </div>
            <div style={{ marginBottom: 20, fontSize: 12, color: 'var(--muted)' }}>
              Antes de continuar, aceptá los siguientes documentos.
            </div>
            {pendingDocuments.map(doc => (
              <label key={doc.document} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!checkedDocs[doc.document]}
                  onChange={e => setCheckedDocs(c => ({ ...c, [doc.document]: e.target.checked }))}
                  style={{ width: 16, height: 16, cursor: 'pointer', marginTop: 2 }}
                />
                <span style={{ fontSize: 13, fontFamily: 'var(--mono)', color: 'var(--text)' }}>
                  Acepto {doc.title} (versión {doc.version}) —{' '}
                  <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>
                    ver documento
                  </a>
                </span>
              </label>
            ))}
            <button
              onClick={handleAcceptTerms}
              disabled={loading || pendingDocuments.some(d => !checkedDocs[d.document])}
              style={{
                ...btnStyle,
                background: loading || pendingDocuments.some(d => !checkedDocs[d.document]) ? 'var(--accent2)' : 'var(--accent)',
                cursor: pendingDocuments.some(d => !checkedDocs[d.document]) ? 'not-allowed' : 'pointer',
                opacity: pendingDocuments.some(d => !checkedDocs[d.document]) ? 0.6 : 1,
              }}
            >
              {loading ? 'GUARDANDO...' : 'ACEPTAR Y CONTINUAR →'}
            </button>
            <button onClick={handleExitTerms}
              style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--muted)', padding: '10px 0 0', fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer', letterSpacing: 1 }}>
              Salir
            </button>
          </>
        )}
      </div>

      {recoveryCodesModal && (
        <RecoveryCodesModal codes={recoveryCodesModal} onClose={closeRecoveryCodesModal} />
      )}
    </div>
  );
}
